import { observer } from 'mobx-react'
import React, { FC, ReactNode, useEffect, useState } from 'react'

import { CheckPickerItemType } from '@avantstay/backoffice-core'

import { RegionsStore } from '@stores/RegionsStore'

import * as S from './RegionsFilter.styles'

interface RegionsFilterProps {
  dataCy?: string
  initialValue?: string[]
  hideSelectAllOption?: boolean
  onChange: (value?: string[]) => void
  noRegionOption?: boolean
  placeholder?: string
  filterTagLimit?: number
  className?: string
}

const handleSearchByRegions = (
  keyword: string,
  _label: ReactNode,
  item: CheckPickerItemType,
): boolean => {
  if (keyword?.length > 0) {
    const normalizeText = (text: string) =>
      text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9\s]+/g, '')
        .toLowerCase()

    const labelString = item.label ? normalizeText(item.label.toString()) : undefined
    const keywordString = normalizeText(keyword)

    if (!keywordString) return false

    return Boolean(labelString?.includes(keywordString)) // Use `includes` instead of regex
  }

  return true
}

/**
 * @deprecated import RegionsFilterNew from @new/__global/components/RegionsFilter instead
 */
export const RegionsFilter: FC<RegionsFilterProps> = observer(
  ({
    className,
    dataCy,
    initialValue,
    hideSelectAllOption,
    noRegionOption,
    onChange,
    placeholder,
    filterTagLimit = 2,
  }) => {
    const { regionsCheckPickerOptions: regionsOptions, isLoadingRegionOptions } = RegionsStore

    const allOptions = noRegionOption
      ? [
          { value: '00000000-0000-0000-0000-000000000000', label: 'No region' },
          ...(regionsOptions || []),
        ]
      : regionsOptions

    const [selectedRegions, setSelectedRegions] = useState<string[] | undefined>(initialValue)

    // This clears selection when we run PropertiesListStore.resetFilters
    useEffect(() => {
      if (initialValue === undefined) {
        setSelectedRegions(undefined)
      }
      if (!!initialValue && initialValue !== selectedRegions) {
        setSelectedRegions(initialValue)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue])

    const regionsLength = regionsOptions?.length

    const inputPlaceholder =
      placeholder ?? `All Markets ${regionsLength > 0 ? `(${regionsLength})` : ''}`

    return (
      <S.CheckPicker
        className={className}
        tagLimit={filterTagLimit}
        data-cy={dataCy}
        searchable
        options={allOptions || []}
        placeholder={inputPlaceholder}
        value={selectedRegions || []}
        onClean={() => {
          setSelectedRegions(undefined)
          onChange(undefined)
        }}
        onClose={() => {
          if (selectedRegions !== initialValue) {
            onChange(selectedRegions)
          }
        }}
        searchBy={handleSearchByRegions}
        onSelect={(value: string[]) => {
          setSelectedRegions(value)
        }}
        isLoadingOptions={isLoadingRegionOptions}
        hideAllOption={hideSelectAllOption}
      />
    )
  },
)
